import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { ElPagination } from "element-plus";

type OneTegPaginationEmits = {
  (e: "update:currentPage", v: number): void;
  (e: "update:pageSize", v: number): void;
  (e: "current-change", v: number): void;
  (e: "size-change", v: number): void;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'OneTegPagination',
  props: {
    total: {},
    currentPage: {},
    pageSize: {},
    pageSizes: { default: () => [10, 25, 50, 100] },
    layout: { default: "total, sizes, prev, pager, next" },
    singlePageLayout: { default: "total, sizes" },
    small: { type: Boolean, default: true },
    background: { type: Boolean, default: true },
    hideOnSinglePage: { type: Boolean, default: false },
    resetToFirstOnSizeChange: { type: Boolean, default: true },
    variant: { default: "default" },
    pageSizeSelectId: { default: "pagination-page-size" }
  },
  emits: ["update:currentPage", "update:pageSize", "current-change", "size-change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const currentPageLocal = computed({
  get: () => props.currentPage,
  set: (v: number) => emit("update:currentPage", v),
});

const pageSizeLocal = computed({
  get: () => props.pageSize,
  set: (v: number) => emit("update:pageSize", v),
});

const pageCount = computed(() => {
  const size = Math.max(1, pageSizeLocal.value || 1);
  return Math.max(1, Math.ceil((props.total || 0) / size));
});

const effectiveLayout = computed(() =>
  pageCount.value <= 1 ? props.singlePageLayout : props.layout
);

function onSizeChange(v: number) {
  if (props.resetToFirstOnSizeChange) emit("update:currentPage", 1);
  emit("size-change", v);
}
function onCurrentChange(v: number) {
  emit("current-change", v);
}

// OT-4908: Element Plus 1.x's ElPagination exposes no id/slot for its
// internal page-size <el-select> (sizes.vue hardcodes it), so there's no
// prop-based way to give it one. A MutationObserver assigns the id
// directly on the rendered DOM node instead, and re-assigns it whenever
// Element Plus re-renders that node (e.g. on a size/layout change),
// without touching ElPagination's own markup, styling, or i18n.
//
// The ref sits on ElPagination itself rather than on a wrapper element:
// app.scss's `.pagination-wrapper .el-pagination { flex: 1 }` only takes
// effect while .el-pagination is a direct flex child of the wrapper, and
// that rule is what spreads the total to the left edge and the page-size
// select to the right on every page using this component.
const paginationRef = ref<{ $el?: HTMLElement | null } | null>(null);
let sizesObserver: MutationObserver | null = null;

// ElPagination renders a plain <div> root, but returns null — leaving $el
// a comment node — for an empty layout, hence the instanceof guard.
function paginationEl(): HTMLElement | null {
  const el = paginationRef.value?.$el;
  return el instanceof HTMLElement ? el : null;
}

function assignPageSizeSelectId() {
  const input = paginationEl()?.querySelector<HTMLInputElement>(
    ".el-pagination__sizes .el-input__inner"
  );
  if (input && input.id !== props.pageSizeSelectId) {
    input.id = props.pageSizeSelectId;
  }
}

// A DOM id has to stay unique, and this one is baked into a component that
// can be instantiated more than once per page (a dialog table over a list
// page, say) — hence the pageSizeSelectId prop. Every current call site
// uses the default.
watch(() => props.pageSizeSelectId, assignPageSizeSelectId);

onMounted(() => {
  assignPageSizeSelectId();
  const root = paginationEl();
  if (root) {
    sizesObserver = new MutationObserver(assignPageSizeSelectId);
    sizesObserver.observe(root, {
      childList: true,
      subtree: true,
    });
  }
});

onBeforeUnmount(() => {
  if (sizesObserver) {
    sizesObserver.disconnect();
    sizesObserver = null;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ElPagination), {
    ref_key: "paginationRef",
    ref: paginationRef,
    class: "oneTeg-pagination",
    "current-page": currentPageLocal.value,
    "onUpdate:currentPage": _cache[0] || (_cache[0] = ($event: any) => ((currentPageLocal).value = $event)),
    "page-size": pageSizeLocal.value,
    "page-sizes": _ctx.pageSizes,
    total: _ctx.total,
    layout: effectiveLayout.value,
    small: _ctx.small,
    background: _ctx.background,
    "hide-on-single-page": false,
    onSizeChange: onSizeChange,
    onCurrentChange: onCurrentChange
  }, null, 8, ["current-page", "page-size", "page-sizes", "total", "layout", "small", "background"]))
}
}

})